import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function ViralReferral() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Current Referral Status
  const [hasReferralProgram, setHasReferralProgram] = useState('');
  const [referralRate, setReferralRate] = useState('');
  const [wordOfMouthPercentage, setWordOfMouthPercentage] = useState('');

  // Referral Program Setup
  const [incentiveStructure, setIncentiveStructure] = useState('');
  const [incentiveTypes, setIncentiveTypes] = useState([]);
  const [incentiveDetails, setIncentiveDetails] = useState('');
  const [referralEase, setReferralEase] = useState('');

  // Viral Mechanics
  const [socialSharing, setSocialSharing] = useState([]);
  const [shareTriggers, setShareTriggers] = useState([]);
  const [shareableContent, setShareableContent] = useState('');

  // User-Generated Content
  const [hasReviews, setHasReviews] = useState('');
  const [hasCaseStudies, setHasCaseStudies] = useState('');
  const [socialProofStrategy, setSocialProofStrategy] = useState('');

  // Partnerships
  const [hasAffiliates, setHasAffiliates] = useState('');
  const [affiliateDetails, setAffiliateDetails] = useState('');
  const [hasPartnerships, setHasPartnerships] = useState('');
  const [partnershipStrategy, setPartnershipStrategy] = useState('');

  const incentiveTypeOptions = ['Discount', 'Cash/Credit', 'Free Month/Service', 'Exclusive Access', 'Physical Gift', 'Donation to Charity'];
  const socialSharingOptions = ['One-Click Share Buttons', 'Pre-Written Social Posts', 'Shareable Graphics/Images', 'Unique Referral Links', 'Email Invitation System', 'Social Login Integration'];
  const shareTriggerOptions = ['After Purchase', 'After Success/Win', 'After Positive Feedback', 'Milestone Achievement', 'End of Trial', 'Regular Prompts'];

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    await fetchSavedStrategy();
    setLoading(false);
  };

  const fetchSavedStrategy = async () => {
    try {
      const response = await fetch('/api/marketing/get-viral-referral', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.viral_referral) {
          const v = data.viral_referral;
          setHasReferralProgram(v.has_referral_program || '');
          setReferralRate(v.referral_rate || '');
          setWordOfMouthPercentage(v.word_of_mouth_percentage || '');
          setIncentiveStructure(v.incentive_structure || '');
          setIncentiveTypes(v.incentive_types || []);
          setIncentiveDetails(v.incentive_details || '');
          setReferralEase(v.referral_ease || '');
          setSocialSharing(v.social_sharing || []);
          setShareTriggers(v.share_triggers || []);
          setShareableContent(v.shareable_content || '');
          setHasReviews(v.has_reviews || '');
          setHasCaseStudies(v.has_case_studies || '');
          setSocialProofStrategy(v.social_proof_strategy || '');
          setHasAffiliates(v.has_affiliates || '');
          setAffiliateDetails(v.affiliate_details || '');
          setHasPartnerships(v.has_partnerships || '');
          setPartnershipStrategy(v.partnership_strategy || '');
        }
      }
    } catch (err) {
      console.log('No saved viral/referral strategy found');
    }
  };

  const handleIncentiveTypeToggle = (type) => {
    if (incentiveTypes.includes(type)) {
      setIncentiveTypes(incentiveTypes.filter(t => t !== type));
    } else {
      setIncentiveTypes([...incentiveTypes, type]);
    }
  };

  const handleSocialSharingToggle = (option) => {
    if (socialSharing.includes(option)) {
      setSocialSharing(socialSharing.filter(o => o !== option));
    } else {
      setSocialSharing([...socialSharing, option]);
    }
  };

  const handleShareTriggerToggle = (trigger) => {
    if (shareTriggers.includes(trigger)) {
      setShareTriggers(shareTriggers.filter(t => t !== trigger));
    } else {
      setShareTriggers([...shareTriggers, trigger]);
    }
  };

  const handleSaveAndContinue = async () => {
    // Basic validation
    if (!hasReferralProgram) {
      setError('Please indicate if you have a referral program');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/save-viral-referral', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          viral_referral: {
            has_referral_program: hasReferralProgram,
            referral_rate: referralRate,
            word_of_mouth_percentage: wordOfMouthPercentage,
            incentive_structure: incentiveStructure,
            incentive_types: incentiveTypes,
            incentive_details: incentiveDetails,
            referral_ease: referralEase,
            social_sharing: socialSharing,
            share_triggers: shareTriggers,
            shareable_content: shareableContent,
            has_reviews: hasReviews,
            has_case_studies: hasCaseStudies,
            social_proof_strategy: socialProofStrategy,
            has_affiliates: hasAffiliates,
            affiliate_details: affiliateDetails,
            has_partnerships: hasPartnerships,
            partnership_strategy: partnershipStrategy
          }
        })
      });

      if (response.ok) {
        navigate('/dashboard/marketing-strategy');
      } else {
        const errorData = await response.json();
        console.error('Save error:', errorData);
        setError(errorData.error || 'Failed to save viral/referral strategy');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Network error:', err);
      setError('Error saving viral/referral strategy: ' + err.message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="marketing-strategy" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-3">Viral Mechanics & Referral Strategy</h1>
              <p className="text-center text-muted mb-4">
                Turn your happy customers into your best marketing channel through referrals and word-of-mouth.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {/* Section 1: Current Referral Status */}
              <div className="mb-5">
                <h3 className="mb-3">Current Referral Status</h3>

                <Form.Group className="mb-3">
                  <Form.Label>Do you currently have a referral program?</Form.Label>
                  <Form.Select
                    value={hasReferralProgram}
                    onChange={(e) => setHasReferralProgram(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-active">Yes, active and promoted</option>
                    <option value="yes-exists">Yes, but not actively promoted</option>
                    <option value="informal">Informal only (word-of-mouth happens naturally)</option>
                    <option value="no">No referral program</option>
                  </Form.Select>
                </Form.Group>

                {(hasReferralProgram === 'yes-active' || hasReferralProgram === 'yes-exists' || hasReferralProgram === 'informal') && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>How many new customers come from referrals?</Form.Label>
                      <Form.Select
                        value={referralRate}
                        onChange={(e) => setReferralRate(e.target.value)}
                      >
                        <option value="">Select approximate rate...</option>
                        <option value="50%+">50%+ of new customers</option>
                        <option value="25-50%">25-50% of new customers</option>
                        <option value="10-25%">10-25% of new customers</option>
                        <option value="5-10%">5-10% of new customers</option>
                        <option value="under-5%">Under 5% of new customers</option>
                        <option value="unknown">I don't track this</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>What percentage of your marketing comes from word-of-mouth?</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., 30% or 'most of it'"
                        value={wordOfMouthPercentage}
                        onChange={(e) => setWordOfMouthPercentage(e.target.value)}
                      />
                      <Form.Text className="text-muted">
                        Include organic referrals, social mentions, reviews, etc.
                      </Form.Text>
                    </Form.Group>
                  </>
                )}
              </div>

              {/* Section 2: Referral Program Setup */}
              <div className="mb-5">
                <h3 className="mb-3">Referral Program Design</h3>
                <p className="text-muted mb-3">
                  The best referral programs reward both the referrer AND the new customer.
                </p>

                <Form.Group className="mb-3">
                  <Form.Label>What's your referral incentive structure?</Form.Label>
                  <Form.Select
                    value={incentiveStructure}
                    onChange={(e) => setIncentiveStructure(e.target.value)}
                  >
                    <option value="">Select structure...</option>
                    <option value="both-sides">Both sides get rewarded (recommended!)</option>
                    <option value="referrer-only">Only referrer gets rewarded</option>
                    <option value="referee-only">Only new customer gets rewarded</option>
                    <option value="tiered">Tiered rewards (more referrals = better rewards)</option>
                    <option value="none">No formal incentives</option>
                  </Form.Select>
                </Form.Group>

                {incentiveStructure && incentiveStructure !== 'none' && (
                  <>
                    <div className="mb-3">
                      <Form.Label>What type of incentives do you offer?</Form.Label>
                      {incentiveTypeOptions.map(type => (
                        <Form.Check
                          key={type}
                          type="checkbox"
                          label={type}
                          checked={incentiveTypes.includes(type)}
                          onChange={() => handleIncentiveTypeToggle(type)}
                        />
                      ))}
                    </div>

                    <Form.Group className="mb-3">
                      <Form.Label>Describe your incentive details</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="e.g., Referrer gets $50 credit, new customer gets 20% off first month. After 5 referrals, referrer gets free month."
                        value={incentiveDetails}
                        onChange={(e) => setIncentiveDetails(e.target.value)}
                      />
                    </Form.Group>
                  </>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>How easy is it for customers to refer others?</Form.Label>
                  <Form.Select
                    value={referralEase}
                    onChange={(e) => setReferralEase(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="very-easy">Very easy (1-click share)</option>
                    <option value="easy">Easy (simple form or link)</option>
                    <option value="moderate">Moderate effort required</option>
                    <option value="difficult">Difficult (manual process)</option>
                    <option value="no-system">No system in place</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    The easier it is, the more referrals you'll get!
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Section 3: Viral Mechanics */}
              <div className="mb-5">
                <h3 className="mb-3">Viral Mechanics & Social Sharing</h3>
                <p className="text-muted mb-3">
                  Make it stupid-easy for customers to share their wins and experiences.
                </p>

                <div className="mb-3">
                  <Form.Label>What social sharing features do you have?</Form.Label>
                  {socialSharingOptions.map(option => (
                    <Form.Check
                      key={option}
                      type="checkbox"
                      label={option}
                      checked={socialSharing.includes(option)}
                      onChange={() => handleSocialSharingToggle(option)}
                    />
                  ))}
                </div>

                <div className="mb-3">
                  <Form.Label>When do you prompt users to share?</Form.Label>
                  {shareTriggerOptions.map(trigger => (
                    <Form.Check
                      key={trigger}
                      type="checkbox"
                      label={trigger}
                      checked={shareTriggers.includes(trigger)}
                      onChange={() => handleShareTriggerToggle(trigger)}
                    />
                  ))}
                </div>

                <Form.Group className="mb-3">
                  <Form.Label>What can customers share?</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="e.g., Their results/achievements, before/after transformations, testimonials, progress reports, exclusive content"
                    value={shareableContent}
                    onChange={(e) => setShareableContent(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Make it about THEIR success, not your product
                  </Form.Text>
                </Form.Group>
              </div>

              {/* Section 4: User-Generated Content */}
              <div className="mb-5">
                <h3 className="mb-3">User-Generated Content & Social Proof</h3>

                <Form.Group className="mb-3">
                  <Form.Label>Do you actively collect reviews and testimonials?</Form.Label>
                  <Form.Select
                    value={hasReviews}
                    onChange={(e) => setHasReviews(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-automated">Yes, automated system</option>
                    <option value="yes-manual">Yes, manually request them</option>
                    <option value="passive">Passively (customers leave them on their own)</option>
                    <option value="no">No, don't actively collect</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Do you create case studies or success stories?</Form.Label>
                  <Form.Select
                    value={hasCaseStudies}
                    onChange={(e) => setHasCaseStudies(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-regular">Yes, regularly publish new ones</option>
                    <option value="yes-occasional">Yes, occasionally</option>
                    <option value="planned">Planning to create them</option>
                    <option value="no">No case studies</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>How do you leverage social proof?</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="e.g., Display testimonials on website, share customer wins on social media, feature success stories in email campaigns, showcase metrics (1000+ customers, 4.8 stars)"
                    value={socialProofStrategy}
                    onChange={(e) => setSocialProofStrategy(e.target.value)}
                  />
                </Form.Group>
              </div>

              {/* Section 5: Partnerships & Affiliates */}
              <div className="mb-4">
                <h3 className="mb-3">Partnerships & Affiliate Programs</h3>

                <Form.Group className="mb-3">
                  <Form.Label>Do you have an affiliate program?</Form.Label>
                  <Form.Select
                    value={hasAffiliates}
                    onChange={(e) => setHasAffiliates(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-active">Yes, active with multiple affiliates</option>
                    <option value="yes-small">Yes, small/starting out</option>
                    <option value="planned">Planning to launch one</option>
                    <option value="no">No affiliate program</option>
                  </Form.Select>
                </Form.Group>

                {(hasAffiliates === 'yes-active' || hasAffiliates === 'yes-small') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Describe your affiliate program</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="e.g., 20% recurring commission, dedicated affiliate dashboard, marketing materials provided, monthly payouts"
                      value={affiliateDetails}
                      onChange={(e) => setAffiliateDetails(e.target.value)}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Do you have strategic partnerships or co-marketing relationships?</Form.Label>
                  <Form.Select
                    value={hasPartnerships}
                    onChange={(e) => setHasPartnerships(e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="yes-multiple">Yes, multiple active partnerships</option>
                    <option value="yes-few">Yes, a few partnerships</option>
                    <option value="exploring">Exploring opportunities</option>
                    <option value="no">No partnerships</option>
                  </Form.Select>
                </Form.Group>

                {(hasPartnerships === 'yes-multiple' || hasPartnerships === 'yes-few') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Describe your partnership strategy</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="e.g., Partner with complementary businesses, joint webinars, bundle offerings, cross-promotion on social media"
                      value={partnershipStrategy}
                      onChange={(e) => setPartnershipStrategy(e.target.value)}
                    />
                  </Form.Group>
                )}
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/marketing-strategy')}
                >
                  ← Back to Dashboard
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={saving}
                  onClick={handleSaveAndContinue}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save & Continue →'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ViralReferral;
